import React, { useState, useEffect } from 'react';
import { 
  Check, X, CheckCircle2, ShieldCheck, ArrowRight, 
  ChevronDown, MessageCircle, Building2,
  ShoppingCart, Users, Laptop, CreditCard, Bell, QrCode,
  Loader2, AlertCircle, Lock, Code2, Smartphone
} from 'lucide-react';
import SEO from './seo/SEO';
import { db } from './firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { sendNotificationEmail } from './utils/emailService';

// ============================================================================
// 1. G-TRACK 360 HRM SOFTWARE PLANS (People, Attendance & Payroll)
// ============================================================================
const HRM_PLANS = {
  yearly: [
    {
      id: 'hrm-startup',
      name: 'Startup HR',
      badge: 'Micro & Small Business',
      desc: 'Ideal for small offices, clinics, retail shops, and startups setting up automated attendance and payroll.',
      originalPrice: '₹11,999',
      price: '₹8,499',
      period: '/ 1st Year',
      renewal: 'Renewal from 2nd year: ₹2,999 + GST / year',
      popular: false,
      capacity: 'Up to 25 Staff',
      features: [
        'Up to 25 Active Employees',
        'Mobile App GPS Geo-fenced Attendance',
        'Employee Self-Service (ESS) Mobile App',
        'Leave Application & Approval Workflow',
        'Basic One-Click Monthly Payroll',
        'Digital Payslip Download & Email',
        'Public Holiday & Shift Scheduling',
        'Standard Email & Ticket Support',
      ],
      notIncluded: [
        'AI Selfie Face-Verification Punch',
        'Statutory PF, ESI, TDS, PT Automated Filing',
        'Biometric Device Hardware Sync',
        'WhatsApp Automated Payslip Disbursal',
        'Multi-Branch Consolidated Dashboard',
      ]
    },
    {
      id: 'hrm-business',
      name: 'Business Growth',
      badge: 'Growing & Mid-Size Teams',
      desc: 'Complete workforce management for growing companies requiring biometric hardware sync and statutory compliance.',
      originalPrice: '₹21,999',
      price: '₹15,499',
      period: '/ 1st Year',
      renewal: 'Renewal from 2nd year: ₹4,499 + GST / year',
      popular: true,
      capacity: 'Up to 100 Staff',
      features: [
        'Up to 100 Employees & 5 Admin Roles',
        'AI Selfie Face-Match GPS Punch',
        'Biometric Device API Sync (ZKTeco/Essl/Matrix)',
        'Full Payroll Engine with PF, ESI, TDS & PT',
        'WhatsApp & Email Payslip Delivery',
        'Multi-Branch Geofenced Locations',
        'Shift Roster & Night Shift Allowance Engine',
        'Employee Expense Claims & Advance Log',
        'Priority 24/7 Phone & WhatsApp Support',
        'Free Setup & Admin Training Session',
      ],
      notIncluded: [
        'Custom REST API & Webhooks',
        'Dedicated Cloud Tenant / On-Premise Server',
      ]
    },
    {
      id: 'hrm-enterprise',
      name: 'Enterprise 360',
      badge: 'Medium & Large Enterprises',
      desc: 'Engineered for multi-site organizations, manufacturing plants, and corporations requiring advanced workforce controls.',
      originalPrice: '₹36,999',
      price: '₹26,999',
      period: '/ 1st Year',
      renewal: 'Renewal from 2nd year: ₹6,999 + GST / year',
      popular: false,
      capacity: 'Up to 300 Staff',
      features: [
        'Up to 300 Employees & Unlimited Locations',
        'All Features of Business Growth Plan',
        'Supervisor / Manager Mobile Approval App',
        'Internal HR Helpdesk & SLA Ticketing',
        'Performance Appraisal (KPIs & OKRs)',
        'Asset Allocation & Return Tracking',
        'Custom Overtime & Late Penalty Matrix',
        'Audit-Ready Compliance & Payroll Exports',
        'Dedicated Implementation Specialist',
      ],
      notIncluded: [
        'Dedicated On-Premise Local Server Install',
      ]
    },
    {
      id: 'hrm-unlimited',
      name: 'Enterprise Max',
      badge: 'Unlimited Scale',
      desc: 'Tailored enterprise deployment for large corporations with dedicated cloud tenants, custom workflows, and full integrations.',
      originalPrice: '₹64,999',
      price: '₹48,999',
      period: '/ 1st Year',
      renewal: 'Renewal from 2nd year: ₹9,999 + GST / year',
      popular: false,
      capacity: 'Unlimited Staff',
      features: [
        'Unlimited Employees & Unlimited Entities',
        'Dedicated Isolated Cloud or Local Server',
        'Full Open REST APIs & Webhook Subscriptions',
        'Microsoft Teams, Slack & Tally ERP Sync',
        'Fine-Grained Role-Based Access (RBAC)',
        'Custom Feature Development Support',
        '256-Bit Military Grade Data Encryption',
        'Dedicated 24/7 Account Manager & SLA Guarantee',
      ],
      notIncluded: []
    }
  ],
  monthly: [
    {
      id: 'hrm-startup-m',
      name: 'Startup HR',
      badge: 'Micro & Small Business',
      desc: 'Ideal for small offices, clinics, retail shops, and startups setting up automated attendance and payroll.',
      originalPrice: '₹1,499',
      price: '₹999',
      period: '/ month',
      renewal: 'Billed monthly • Cancel anytime',
      popular: false,
      capacity: 'Up to 25 Staff',
      features: [
        'Up to 25 Active Employees',
        'Mobile App GPS Geo-fenced Attendance',
        'Employee Self-Service (ESS) Mobile App',
        'Leave Application & Approval Workflow',
        'Basic One-Click Monthly Payroll',
        'Digital Payslip Download & Email',
        'Public Holiday & Shift Scheduling',
        'Standard Email & Ticket Support',
      ],
      notIncluded: [
        'AI Selfie Face-Verification Punch',
        'Statutory PF, ESI, TDS, PT Automated Filing',
        'Biometric Device Hardware Sync',
        'WhatsApp Automated Payslip Disbursal',
        'Multi-Branch Consolidated Dashboard',
      ]
    },
    {
      id: 'hrm-business-m',
      name: 'Business Growth',
      badge: 'Growing & Mid-Size Teams',
      desc: 'Complete workforce management for growing companies requiring biometric hardware sync and statutory compliance.',
      originalPrice: '₹2,499',
      price: '₹1,799',
      period: '/ month',
      renewal: 'Billed monthly • Priority Cloud',
      popular: true,
      capacity: 'Up to 100 Staff',
      features: [
        'Up to 100 Employees & 5 Admin Roles',
        'AI Selfie Face-Match GPS Punch',
        'Biometric Device API Sync (ZKTeco/Essl/Matrix)',
        'Full Payroll Engine with PF, ESI, TDS & PT',
        'WhatsApp & Email Payslip Delivery',
        'Multi-Branch Geofenced Locations',
        'Shift Roster & Night Shift Allowance Engine',
        'Employee Expense Claims & Advance Log',
        'Priority 24/7 Phone & WhatsApp Support',
        'Free Setup & Admin Training Session',
      ],
      notIncluded: [
        'Custom REST API & Webhooks',
        'Dedicated Cloud Tenant / On-Premise Server',
      ]
    },
    {
      id: 'hrm-enterprise-m',
      name: 'Enterprise 360',
      badge: 'Medium & Large Enterprises',
      desc: 'Engineered for multi-site organizations, manufacturing plants, and corporations requiring advanced workforce controls.',
      originalPrice: '₹3,999',
      price: '₹2,999',
      period: '/ month',
      renewal: 'Billed monthly • Dedicated Support',
      popular: false,
      capacity: 'Up to 300 Staff',
      features: [
        'Up to 300 Employees & Unlimited Locations',
        'All Features of Business Growth Plan',
        'Supervisor / Manager Mobile Approval App',
        'Internal HR Helpdesk & SLA Ticketing',
        'Performance Appraisal (KPIs & OKRs)',
        'Asset Allocation & Return Tracking',
        'Custom Overtime & Late Penalty Matrix',
        'Audit-Ready Compliance & Payroll Exports',
        'Dedicated Implementation Specialist',
      ],
      notIncluded: [
        'Dedicated On-Premise Local Server Install',
      ]
    },
    {
      id: 'hrm-unlimited-m',
      name: 'Enterprise Max',
      badge: 'Unlimited Scale',
      desc: 'Tailored enterprise deployment for large corporations with dedicated cloud tenants, custom workflows, and full integrations.',
      originalPrice: '₹6,999',
      price: '₹5,499',
      period: '/ month',
      renewal: 'Billed monthly • SLA 99.9%',
      popular: false,
      capacity: 'Unlimited Staff',
      features: [
        'Unlimited Employees & Unlimited Entities',
        'Dedicated Isolated Cloud or Local Server',
        'Full Open REST APIs & Webhook Subscriptions',
        'Microsoft Teams, Slack & Tally ERP Sync',
        'Fine-Grained Role-Based Access (RBAC)',
        'Custom Feature Development Support',
        '256-Bit Military Grade Data Encryption',
        'Dedicated 24/7 Account Manager & SLA Guarantee',
      ],
      notIncluded: []
    }
  ]
};

// ============================================================================
// 2. G-TRACK RETAIL & POS BILLING SOFTWARE PLANS (Invoicing, Inventory & ERP)
// Hybrid Dual Storage (Online + Offline) + Dedicated Owner Admin Portal
// ============================================================================
const BILLING_PLANS = {
  yearly: [
    {
      id: 'bill-offline',
      name: 'Startup Retail POS',
      badge: 'Hybrid Dual Storage • 1 Counter',
      desc: 'Superfast counter billing software with hybrid dual storage: works 100% offline with zero internet cuts, plus automated cloud backup and mobile admin check.',
      originalPrice: '₹13,999',
      price: '₹9,999',
      period: '/ 1st Year',
      renewal: 'Renewal from 2nd year: ₹3,499 + GST / year',
      popular: false,
      capacity: '1 Counter + Owner Admin App',
      features: [
        'Hybrid Dual Storage (100% Offline Billing + Cloud Sync)',
        'Owner Mobile Admin Portal to check live sales & cash anytime',
        'Single Counter Desktop Installation + Fast Barcode Billing (< 2s)',
        'Thermal Receipt & A4/A5 Laser GST Invoicing with HSN Lookup',
        'Item Master, Barcode Printing & Automatic Stock Tracking',
        'Daily Sales, Counter Cash Balance & Profit Margin Reports',
        'Customer Directory & Instant WhatsApp Digital Bill Delivery',
        'Standard Phone & Remote AnyDesk Onboarding Support',
      ],
      notIncluded: [
        'Multi-Counter Local LAN Sync',
        'Customer Loyalty Points & Cashback Engine',
        'Stock Audit Mobile App (Scan via Phone)',
        'E-Commerce Online Store Integration',
      ]
    },
    {
      id: 'bill-online',
      name: 'Standard Store Pro',
      badge: 'Dual Storage • 3 Counters + Admin',
      desc: 'Cloud + Offline connected POS for modern retail stores with dedicated owner admin dashboard to monitor live sales, stock, and customer khata.',
      originalPrice: '₹23,999',
      price: '₹17,499',
      period: '/ 1st Year',
      renewal: 'Renewal from 2nd year: ₹4,499 + GST / year',
      popular: false,
      capacity: '3 Users • 1 Store + Admin Portal',
      features: [
        'Hybrid Dual Storage: Offline Counters + Real-Time Cloud Sync',
        'Executive Owner Admin Dashboard: Live sales check from phone or laptop',
        'Up to 3 Users / Billing Terminals with Local LAN Backup',
        'Automated WhatsApp Bill Delivery with Customer Ledger',
        'Item-Level Barcode & Batch/Expiry Date Tracking',
        'Purchase Order (PO) & Supplier Inward Khata Ledger',
        'Low Stock Warning & Re-Order Point Alerts',
        'E-Way Bill & E-Invoice Generation Assistance',
      ],
      notIncluded: [
        'Customer Loyalty Points & Cashback Engine',
        'Payment EDC Swipe Machine Integration',
        'Central Multi-Warehouse Sync',
      ]
    },
    {
      id: 'bill-premium',
      name: 'Premium Retail ERP',
      badge: 'Best Seller • Multi-Counter + Admin',
      desc: 'The complete retail power pack with loyalty programs, EDC swipe integration, dual storage, and full owner admin controls.',
      originalPrice: '₹38,999',
      price: '₹28,999',
      period: '/ 1st Year',
      renewal: 'Renewal from 2nd year: ₹6,999 + GST / year',
      popular: true,
      capacity: '10 Users • Multi-Counter + Admin',
      features: [
        'Hybrid Dual Storage: All counters work offline with auto cloud master sync',
        'Full Owner Admin Suite: Live counter oversight, audit logs & profit charts',
        'Up to 10 Users & Multiple Billing Counters simultaneously',
        'Customer Loyalty Points, Rewards & Cashback Engine',
        'Promotional Scheme Engine (BOGO, % Off, Category Bundles)',
        'Payment EDC Swipe Machine Integration (PhonePe, Pine Labs)',
        'Stock Audit Mobile App (Scan with Phone Camera)',
        'Multi-Price Level (Retail, Wholesale, Distributor)',
        'GSTR-1, GSTR-3B & Tax Audit Export (Excel/JSON)',
        'Priority Phone, WhatsApp & Dedicated Implementation',
      ],
      notIncluded: [
        'Central Multi-Warehouse Sync',
        'E-Commerce Online Catalog Website',
      ]
    },
    {
      id: 'bill-enterprise',
      name: 'Enterprise Omnichannel ERP',
      badge: 'Multi-Store Chains • Central HQ Admin',
      desc: 'Complete enterprise ERP solution for retail chains, connecting physical counters with online e-commerce and centralized HQ admin management.',
      originalPrice: '₹69,999',
      price: '₹52,999',
      period: '/ 1st Year',
      renewal: 'Renewal from 2nd year: ₹9,999 + GST / year',
      popular: false,
      capacity: 'Unlimited Users • Multi-Store + HQ Admin',
      features: [
        'Enterprise Dual Storage Architecture (Offline Branches + Central Cloud HQ)',
        'Executive Central HQ Admin Portal: Monitor and control all stores in real time',
        'Unlimited Users & Multi-Branch Counter Sync',
        'Central Warehouse Inventory Distribution & Transfer (STN)',
        'Integrated Branded Online E-Commerce Catalog',
        'Live Stock Sync between Physical Counters & Online Store',
        'Delivery Boy Mobile App & Local Order Tracking',
        'Seamless Accounting Export (Tally Prime & Zoho Books)',
        'Role-Based Counter Permissions & Cash Audits',
        'Dedicated Priority Onboarding & Data Migration',
      ],
      notIncluded: []
    }
  ],
  monthly: [
    {
      id: 'bill-offline-m',
      name: 'Startup Retail POS',
      badge: 'Hybrid Dual Storage • 1 Counter',
      desc: 'Superfast counter billing software with hybrid dual storage: works 100% offline with zero internet cuts, plus automated cloud backup and mobile admin check.',
      originalPrice: '₹1,699',
      price: '₹1,199',
      period: '/ month',
      renewal: 'Billed monthly • Cancel anytime',
      popular: false,
      capacity: '1 Counter + Owner Admin App',
      features: [
        'Hybrid Dual Storage (100% Offline Billing + Cloud Sync)',
        'Owner Mobile Admin Portal to check live sales & cash anytime',
        'Single Counter Desktop Installation + Fast Barcode Billing (< 2s)',
        'Thermal Receipt & A4/A5 Laser GST Invoicing with HSN Lookup',
        'Item Master, Barcode Printing & Automatic Stock Tracking',
        'Daily Sales, Counter Cash Balance & Profit Margin Reports',
        'Customer Directory & Instant WhatsApp Digital Bill Delivery',
        'Standard Phone & Remote AnyDesk Onboarding Support',
      ],
      notIncluded: [
        'Multi-Counter Local LAN Sync',
        'Customer Loyalty Points & Cashback Engine',
        'Stock Audit Mobile App (Scan via Phone)',
        'E-Commerce Online Store Integration',
      ]
    },
    {
      id: 'bill-online-m',
      name: 'Standard Store Pro',
      badge: 'Dual Storage • 3 Counters + Admin',
      desc: 'Cloud + Offline connected POS for modern retail stores with dedicated owner admin dashboard to monitor live sales, stock, and customer khata.',
      originalPrice: '₹2,799',
      price: '₹1,999',
      period: '/ month',
      renewal: 'Billed monthly • Cloud Backup',
      popular: false,
      capacity: '3 Users • 1 Store + Admin Portal',
      features: [
        'Hybrid Dual Storage: Offline Counters + Real-Time Cloud Sync',
        'Executive Owner Admin Dashboard: Live sales check from phone or laptop',
        'Up to 3 Users / Billing Terminals with Local LAN Backup',
        'Automated WhatsApp Bill Delivery with Customer Ledger',
        'Item-Level Barcode & Batch/Expiry Date Tracking',
        'Purchase Order (PO) & Supplier Inward Khata Ledger',
        'Low Stock Warning & Re-Order Point Alerts',
        'E-Way Bill & E-Invoice Generation Assistance',
      ],
      notIncluded: [
        'Customer Loyalty Points & Cashback Engine',
        'Payment EDC Swipe Machine Integration',
        'Central Multi-Warehouse Sync',
      ]
    },
    {
      id: 'bill-premium-m',
      name: 'Premium Retail ERP',
      badge: 'Best Seller • Multi-Counter + Admin',
      desc: 'The complete retail power pack with loyalty programs, EDC swipe integration, dual storage, and full owner admin controls.',
      originalPrice: '₹4,499',
      price: '₹3,299',
      period: '/ month',
      renewal: 'Billed monthly • Priority Support',
      popular: true,
      capacity: '10 Users • Multi-Counter + Admin',
      features: [
        'Hybrid Dual Storage: All counters work offline with auto cloud master sync',
        'Full Owner Admin Suite: Live counter oversight, audit logs & profit charts',
        'Up to 10 Users & Multiple Billing Counters simultaneously',
        'Customer Loyalty Points, Rewards & Cashback Engine',
        'Promotional Scheme Engine (BOGO, % Off, Category Bundles)',
        'Payment EDC Swipe Machine Integration (PhonePe, Pine Labs)',
        'Stock Audit Mobile App (Scan with Phone Camera)',
        'Multi-Price Level (Retail, Wholesale, Distributor)',
        'GSTR-1, GSTR-3B & Tax Audit Export (Excel/JSON)',
        'Priority Phone, WhatsApp & Dedicated Implementation',
      ],
      notIncluded: [
        'Central Multi-Warehouse Sync',
        'E-Commerce Online Catalog Website',
      ]
    },
    {
      id: 'bill-enterprise-m',
      name: 'Enterprise Omnichannel ERP',
      badge: 'Multi-Store Chains • Central HQ Admin',
      desc: 'Complete enterprise ERP solution for retail chains, connecting physical counters with online e-commerce and centralized HQ admin management.',
      originalPrice: '₹7,999',
      price: '₹5,999',
      period: '/ month',
      renewal: 'Billed monthly • Dedicated Onboarding',
      popular: false,
      capacity: 'Unlimited Users • Multi-Store + HQ Admin',
      features: [
        'Enterprise Dual Storage Architecture (Offline Branches + Central Cloud HQ)',
        'Executive Central HQ Admin Portal: Monitor and control all stores in real time',
        'Unlimited Users & Multi-Branch Counter Sync',
        'Central Warehouse Inventory Distribution & Transfer (STN)',
        'Integrated Branded Online E-Commerce Catalog',
        'Live Stock Sync between Physical Counters & Online Store',
        'Delivery Boy Mobile App & Local Order Tracking',
        'Seamless Accounting Export (Tally Prime & Zoho Books)',
        'Role-Based Counter Permissions & Cash Audits',
        'Dedicated Priority Onboarding & Data Migration',
      ],
      notIncluded: []
    }
  ]
};

// ============================================================================
// 3. CUSTOM IT ENGINEERING & DIGITAL GROWTH SERVICES PLANS (Section 4)
// ============================================================================
const CUSTOM_IT_PLANS = {
  yearly: [
    // Web Solutions
    {
      id: 'it-web-starter',
      domain: 'Web Solutions',
      name: 'Starter Business Website',
      badge: 'Web Solutions • Small Business',
      desc: 'High-converting responsive website for local companies and service businesses.',
      originalPrice: '₹24,000',
      price: '₹18,999',
      period: '/ 1st Year',
      renewal: 'Save ~20% on Annual Plan',
      popular: false,
      capacity: '5–7 Pages + Fast Hosting',
      features: [
        '5–7 Mobile Responsive Pages',
        'Modern UI/UX & Fast Loading Speed',
        'Free SSL Security Certificate',
        'High-Speed Cloud Hosting Setup',
        'Lead Capture Contact Form & WhatsApp Button',
        'Google Maps & Local Search Optimization',
        'Standard Email & AnyDesk Technical Support',
      ],
      notIncluded: [
        'E-Commerce Cart & Payment Gateway',
        'Custom Admin CMS Dashboard',
      ]
    },
    {
      id: 'it-web-ecommerce',
      domain: 'Web Solutions',
      name: 'Corporate / E-Commerce ⭐',
      badge: 'Web Solutions • Top Seller',
      desc: 'Full-featured online store with payment gateway, catalog management, and admin CMS.',
      originalPrice: '₹48,000',
      price: '₹36,999',
      period: '/ 1st Year',
      renewal: 'Save ~20% on Annual Plan',
      popular: true,
      capacity: 'Dynamic Catalog + CMS',
      features: [
        'Complete Online E-Commerce Store & Cart',
        'Online Payment Gateway (Razorpay/Stripe/UPI)',
        'Admin Dashboard for Products & Order Tracking',
        'Customer Accounts & Order History',
        'Automated Invoice Generation (PDF & Email)',
        'Advanced On-Page SEO & Schema Markup',
        'WhatsApp Automated Order Notifications',
      ],
      notIncluded: [
        'Microservices Architecture',
      ]
    },
    {
      id: 'it-web-saas',
      domain: 'Web Solutions',
      name: 'Enterprise Custom SaaS',
      badge: 'Web Solutions • Enterprise Scale',
      desc: 'Custom web software, client portals, microservices, and bespoke SaaS platforms.',
      originalPrice: '₹95,000',
      price: '₹74,999',
      period: '/ 1st Year',
      renewal: 'Save ~20% on Annual Plan',
      popular: false,
      capacity: 'Custom Architecture',
      features: [
        'Custom Web Portal & Web Application Architecture',
        'Microservices & High-Concurrency Database Design',
        'Full Open REST APIs & Webhook Subscriptions',
        'Role-Based Fine-Grained Permissions (RBAC)',
        'Third-Party ERP & CRM System Integrations',
        'Enterprise Security & Automated Daily Backups',
        'Dedicated Technical Lead & SLA Support',
      ],
      notIncluded: []
    },
    // Mobile Apps
    {
      id: 'it-app-starter',
      domain: 'Mobile Apps',
      name: 'Starter MVP Mobile App',
      badge: 'Mobile Apps • Android/iOS',
      desc: 'Rapid MVP mobile application designed to validate workflows or field operations.',
      originalPrice: '₹50,000',
      price: '₹39,999',
      period: '/ 1st Year',
      renewal: 'Save ~20% on Annual Plan',
      popular: false,
      capacity: 'Single Platform App',
      features: [
        'Native/Hybrid Single Platform (Android or iOS)',
        'Core Functional Workflows & Clean UI',
        'User Phone/OTP & Email Authentication',
        'Cloud Database Integration & Real-time Sync',
        'Push Notifications Engine',
        'App Store / Play Store Deployment Guide',
        'Complimentary Maintenance & Bug Fixes',
      ],
      notIncluded: [
        'Dual Platform (Android & iOS) from Single Code',
        'Hardware Pairing (Bluetooth/NFC)',
      ]
    },
    {
      id: 'it-app-pro',
      domain: 'Mobile Apps',
      name: 'Cross-Platform Pro ⭐',
      badge: 'Mobile Apps • Top Seller',
      desc: 'Cross-platform app delivering top performance on both iPhone and Android devices.',
      originalPrice: '₹95,000',
      price: '₹74,999',
      period: '/ 1st Year',
      renewal: 'Save ~20% on Annual Plan',
      popular: true,
      capacity: 'Both Android & iOS',
      features: [
        'Both iOS and Android from Single Unified Codebase',
        'Firebase / PostgreSQL Cloud Backend Database',
        'Integrated In-App Payments (UPI, Cards, Wallets)',
        'Real-time GPS Tracking & Geolocation Services',
        'Advanced Interactive UI Animations & Offline Cache',
        'Full App Store & Google Play Store Publishing',
        'Dedicated Quarterly Updates & Maintenance',
      ],
      notIncluded: [
        'Enterprise Microservices Infrastructure',
      ]
    },
    {
      id: 'it-app-enterprise',
      domain: 'Mobile Apps',
      name: 'Enterprise Mobile Ecosystem',
      badge: 'Mobile Apps • Full Ecosystem',
      desc: 'Enterprise-grade mobile system with real-time WebSockets, offline sync, and web admin.',
      originalPrice: '₹1,90,000',
      price: '₹1,49,999',
      period: '/ 1st Year',
      renewal: 'Save ~20% on Annual Plan',
      popular: false,
      capacity: 'iOS + Android + Web Admin',
      features: [
        'Dual Mobile Apps (iOS & Android) + Web Admin Console',
        'Offline-First Sync Engine & Real-time WebSockets',
        'Biometric Auth (FaceID / Fingerprint Login)',
        'Bluetooth, NFC & POS Peripheral Hardware Pairing',
        'Custom Notification Server & WhatsApp API Alerts',
        'Automated CI/CD App Build Pipeline',
        '24/7 Enterprise SLA Hotline',
      ],
      notIncluded: []
    },
    // Desktop Software
    {
      id: 'it-desk-single',
      domain: 'Desktop Software',
      name: 'Single-Counter Retail POS',
      badge: 'Desktop Software • Retail & Shop',
      desc: 'Fast offline billing software engineered for standalone billing counters.',
      originalPrice: '₹24,000',
      price: '₹18,999',
      period: '/ 1st Year',
      renewal: 'Save ~20% on Annual Plan',
      popular: false,
      capacity: '1 Desktop Counter',
      features: [
        'Fast Offline Barcode Invoicing (< 2s per bill)',
        'Thermal Receipt & Laser Invoice Printing',
        'Item Inventory & Re-order Alerts',
        'GST Invoicing with Automated HSN Lookup',
        'Runs 100% Offline (No Internet Required)',
        'Automated Local Daily Database Backup',
        'Remote Setup & Staff Training',
      ],
      notIncluded: [
        'Multi-Counter Local LAN Sync',
      ]
    },
    {
      id: 'it-desk-lan',
      domain: 'Desktop Software',
      name: 'Multi-User LAN ERP ⭐',
      badge: 'Desktop Software • Medium ERP',
      desc: 'Desktop ERP connecting billing, stockroom, accounts and managers via local LAN.',
      originalPrice: '₹58,000',
      price: '₹44,999',
      period: '/ 1st Year',
      renewal: 'Save ~20% on Annual Plan',
      popular: true,
      capacity: 'Multi-Terminal LAN',
      features: [
        'Multi-Terminal Local LAN Database Synchronization',
        'Concurrent Multi-User Roles (Cashier, Manager, Admin)',
        'Purchase Orders, Inward Goods & Supplier Khata',
        'Batch & Expiry Date Management System',
        'Barcode Label Designer & Thermal Printing',
        'GSTR-1 & GSTR-3B Tax Filing Report Exports',
        'Automated Redundant Daily Backups',
      ],
      notIncluded: [
        'Central Multi-Branch Cloud HQ',
      ]
    },
    {
      id: 'it-desk-branch',
      domain: 'Desktop Software',
      name: 'Multi-Branch Enterprise ERP',
      badge: 'Desktop Software • Multi-Store HQ',
      desc: 'Centralized enterprise ERP connecting multi-branch counters with central warehouse.',
      originalPrice: '₹1,15,000',
      price: '₹89,999',
      period: '/ 1st Year',
      renewal: 'Save ~20% on Annual Plan',
      popular: false,
      capacity: 'Multi-Branch Cloud Sync',
      features: [
        'Multi-Branch Cloud Sync with Offline LAN Resilience',
        'Central Warehouse Inventory & Stock Transfers (STN)',
        'Central HQ Executive Management Dashboard',
        'Multi-Price Tiers (Retail, Wholesale, Super-Stockist)',
        'Direct Tally Prime Accounting Integration',
        'Role-Based Granular Access & Cash Drawer Audit',
        'Priority Phone & On-Site Support',
      ],
      notIncluded: []
    },
    // Digital Marketing
    {
      id: 'it-mktg-local',
      domain: 'Digital Marketing',
      name: 'Starter SEO & Local Growth',
      badge: 'Marketing • Local SEO & Maps',
      desc: 'Dominate local Google Search, Google Maps, and nearby customer discovery.',
      originalPrice: '₹1,10,000',
      price: '₹89,999',
      period: '/ 1st Year',
      renewal: 'Save ~20% on Annual Plan',
      popular: false,
      capacity: 'Local Map Pack & 10 Keywords',
      features: [
        'Google Business Profile (GBP) Full Optimization',
        'Local Citation Building & NAP Consistency Audit',
        'Targeted 10 High-Intent Local Keywords',
        'On-Page Technical SEO Audit & Fixes',
        'Monthly 4 Branded Social Media Post Creatives',
        'Google Review Growth Funnel Setup',
        'Monthly Performance & Ranking PDF Report',
      ],
      notIncluded: [
        'Paid Google Search Ads Campaigns',
      ]
    },
    {
      id: 'it-mktg-leads',
      domain: 'Digital Marketing',
      name: 'Performance Lead Gen ⭐',
      badge: 'Marketing • High-Intent Leads',
      desc: 'Paid ad campaigns across Google Search & Meta designed for qualified sales inquiries.',
      originalPrice: '₹2,40,000',
      price: '₹1,89,999',
      period: '/ 1st Year',
      renewal: 'Save ~20% on Annual Plan',
      popular: true,
      capacity: 'Google Ads + Meta Lead Funnel',
      features: [
        'Google Search & High-Intent Display Ad Campaigns',
        'Meta (Facebook & Instagram) Targeted Lead Ads',
        'High-Converting Landing Page Design & CRO Audit',
        'Direct WhatsApp Lead Routing & CRM Webhook Sync',
        'Negative Keyword Scrubbing & Daily Bid Tuning',
        'A/B Creative Split Testing & Copywriting',
        'Weekly Lead Cost & ROI Video Reporting',
      ],
      notIncluded: [
        'Omnichannel Brand Leadership',
      ]
    },
    {
      id: 'it-mktg-enterprise',
      domain: 'Digital Marketing',
      name: 'Full-Funnel Enterprise Growth',
      badge: 'Marketing • Market Leaders',
      desc: 'Omnichannel market dominance combining SEO, paid media, and dedicated growth team.',
      originalPrice: '₹4,60,000',
      price: '₹3,69,999',
      period: '/ 1st Year',
      renewal: 'Save ~20% on Annual Plan',
      popular: false,
      capacity: 'Omnichannel B2B Dominance',
      features: [
        'Omnichannel Dominance: Google, Meta, LinkedIn & YouTube',
        'Comprehensive Technical SEO & Content Marketing Funnel',
        'Dedicated Performance Marketing Squad & Copywriter',
        'Automated Email & WhatsApp Retargeting Drip Flows',
        'Competitor Ad Spying & Market Share Acquisition',
        'Conversion Rate Optimization (CRO) & Heatmap Analysis',
        'Bi-Weekly Strategic Growth Consulting Call',
      ],
      notIncluded: []
    }
  ],
  monthly: [
    // Web Solutions
    {
      id: 'it-web-starter-m',
      domain: 'Web Solutions',
      name: 'Starter Business Website',
      badge: 'Web Solutions • Small Business',
      desc: 'High-converting responsive website for local companies and service businesses.',
      originalPrice: '₹2,499',
      price: '₹1,899',
      period: '/ month',
      renewal: 'Billed monthly • Cancel anytime',
      popular: false,
      capacity: '5–7 Pages + Fast Hosting',
      features: [
        '5–7 Mobile Responsive Pages',
        'Modern UI/UX & Fast Loading Speed',
        'Free SSL Security Certificate',
        'High-Speed Cloud Hosting Setup',
        'Lead Capture Contact Form & WhatsApp Button',
        'Google Maps & Local Search Optimization',
        'Standard Email & AnyDesk Technical Support',
      ],
      notIncluded: [
        'E-Commerce Cart & Payment Gateway',
        'Custom Admin CMS Dashboard',
      ]
    },
    {
      id: 'it-web-ecommerce-m',
      domain: 'Web Solutions',
      name: 'Corporate / E-Commerce ⭐',
      badge: 'Web Solutions • Top Seller',
      desc: 'Full-featured online store with payment gateway, catalog management, and admin CMS.',
      originalPrice: '₹4,800',
      price: '₹3,699',
      period: '/ month',
      renewal: 'Billed monthly • Cancel anytime',
      popular: true,
      capacity: 'Dynamic Catalog + CMS',
      features: [
        'Complete Online E-Commerce Store & Cart',
        'Online Payment Gateway (Razorpay/Stripe/UPI)',
        'Admin Dashboard for Products & Order Tracking',
        'Customer Accounts & Order History',
        'Automated Invoice Generation (PDF & Email)',
        'Advanced On-Page SEO & Schema Markup',
        'WhatsApp Automated Order Notifications',
      ],
      notIncluded: [
        'Microservices Architecture',
      ]
    },
    {
      id: 'it-web-saas-m',
      domain: 'Web Solutions',
      name: 'Enterprise Custom SaaS',
      badge: 'Web Solutions • Enterprise Scale',
      desc: 'Custom web software, client portals, microservices, and bespoke SaaS platforms.',
      originalPrice: '₹9,500',
      price: '₹7,499',
      period: '/ month',
      renewal: 'Billed monthly • Cancel anytime',
      popular: false,
      capacity: 'Custom Architecture',
      features: [
        'Custom Web Portal & Web Application Architecture',
        'Microservices & High-Concurrency Database Design',
        'Full Open REST APIs & Webhook Subscriptions',
        'Role-Based Fine-Grained Permissions (RBAC)',
        'Third-Party ERP & CRM System Integrations',
        'Enterprise Security & Automated Daily Backups',
        'Dedicated Technical Lead & SLA Support',
      ],
      notIncluded: []
    },
    // Mobile Apps
    {
      id: 'it-app-starter-m',
      domain: 'Mobile Apps',
      name: 'Starter MVP Mobile App',
      badge: 'Mobile Apps • Android/iOS',
      desc: 'Rapid MVP mobile application designed to validate workflows or field operations.',
      originalPrice: '₹4,999',
      price: '₹3,999',
      period: '/ month',
      renewal: 'Billed monthly • Cancel anytime',
      popular: false,
      capacity: 'Single Platform App',
      features: [
        'Native/Hybrid Single Platform (Android or iOS)',
        'Core Functional Workflows & Clean UI',
        'User Phone/OTP & Email Authentication',
        'Cloud Database Integration & Real-time Sync',
        'Push Notifications Engine',
        'App Store / Play Store Deployment Guide',
        'Complimentary Maintenance & Bug Fixes',
      ],
      notIncluded: [
        'Dual Platform (Android & iOS) from Single Code',
        'Hardware Pairing (Bluetooth/NFC)',
      ]
    },
    {
      id: 'it-app-pro-m',
      domain: 'Mobile Apps',
      name: 'Cross-Platform Pro ⭐',
      badge: 'Mobile Apps • Top Seller',
      desc: 'Cross-platform app delivering top performance on both iPhone and Android devices.',
      originalPrice: '₹9,500',
      price: '₹7,499',
      period: '/ month',
      renewal: 'Billed monthly • Cancel anytime',
      popular: true,
      capacity: 'Both Android & iOS',
      features: [
        'Both iOS and Android from Single Unified Codebase',
        'Firebase / PostgreSQL Cloud Backend Database',
        'Integrated In-App Payments (UPI, Cards, Wallets)',
        'Real-time GPS Tracking & Geolocation Services',
        'Advanced Interactive UI Animations & Offline Cache',
        'Full App Store & Google Play Store Publishing',
        'Dedicated Quarterly Updates & Maintenance',
      ],
      notIncluded: [
        'Enterprise Microservices Infrastructure',
      ]
    },
    {
      id: 'it-app-enterprise-m',
      domain: 'Mobile Apps',
      name: 'Enterprise Mobile Ecosystem',
      badge: 'Mobile Apps • Full Ecosystem',
      desc: 'Enterprise-grade mobile system with real-time WebSockets, offline sync, and web admin.',
      originalPrice: '₹19,000',
      price: '₹14,999',
      period: '/ month',
      renewal: 'Billed monthly • Cancel anytime',
      popular: false,
      capacity: 'iOS + Android + Web Admin',
      features: [
        'Dual Mobile Apps (iOS & Android) + Web Admin Console',
        'Offline-First Sync Engine & Real-time WebSockets',
        'Biometric Auth (FaceID / Fingerprint Login)',
        'Bluetooth, NFC & POS Peripheral Hardware Pairing',
        'Custom Notification Server & WhatsApp API Alerts',
        'Automated CI/CD App Build Pipeline',
        '24/7 Enterprise SLA Hotline',
      ],
      notIncluded: []
    },
    // Desktop Software
    {
      id: 'it-desk-single-m',
      domain: 'Desktop Software',
      name: 'Single-Counter Retail POS',
      badge: 'Desktop Software • Retail & Shop',
      desc: 'Fast offline billing software engineered for standalone billing counters.',
      originalPrice: '₹2,499',
      price: '₹1,899',
      period: '/ month',
      renewal: 'Billed monthly • Cancel anytime',
      popular: false,
      capacity: '1 Desktop Counter',
      features: [
        'Fast Offline Barcode Invoicing (< 2s per bill)',
        'Thermal Receipt & Laser Invoice Printing',
        'Item Inventory & Re-order Alerts',
        'GST Invoicing with Automated HSN Lookup',
        'Runs 100% Offline (No Internet Required)',
        'Automated Local Daily Database Backup',
        'Remote Setup & Staff Training',
      ],
      notIncluded: [
        'Multi-Counter Local LAN Sync',
      ]
    },
    {
      id: 'it-desk-lan-m',
      domain: 'Desktop Software',
      name: 'Multi-User LAN ERP ⭐',
      badge: 'Desktop Software • Medium ERP',
      desc: 'Desktop ERP connecting billing, stockroom, accounts and managers via local LAN.',
      originalPrice: '₹5,800',
      price: '₹4,499',
      period: '/ month',
      renewal: 'Billed monthly • Cancel anytime',
      popular: true,
      capacity: 'Multi-Terminal LAN',
      features: [
        'Multi-Terminal Local LAN Database Synchronization',
        'Concurrent Multi-User Roles (Cashier, Manager, Admin)',
        'Purchase Orders, Inward Goods & Supplier Khata',
        'Batch & Expiry Date Management System',
        'Barcode Label Designer & Thermal Printing',
        'GSTR-1 & GSTR-3B Tax Filing Report Exports',
        'Automated Redundant Daily Backups',
      ],
      notIncluded: [
        'Central Multi-Branch Cloud HQ',
      ]
    },
    {
      id: 'it-desk-branch-m',
      domain: 'Desktop Software',
      name: 'Multi-Branch Enterprise ERP',
      badge: 'Desktop Software • Multi-Store HQ',
      desc: 'Centralized enterprise ERP connecting multi-branch counters with central warehouse.',
      originalPrice: '₹11,500',
      price: '₹8,999',
      period: '/ month',
      renewal: 'Billed monthly • Cancel anytime',
      popular: false,
      capacity: 'Multi-Branch Cloud Sync',
      features: [
        'Multi-Branch Cloud Sync with Offline LAN Resilience',
        'Central Warehouse Inventory & Stock Transfers (STN)',
        'Central HQ Executive Management Dashboard',
        'Multi-Price Tiers (Retail, Wholesale, Super-Stockist)',
        'Direct Tally Prime Accounting Integration',
        'Role-Based Granular Access & Cash Drawer Audit',
        'Priority Phone & On-Site Support',
      ],
      notIncluded: []
    },
    // Digital Marketing
    {
      id: 'it-mktg-local-m',
      domain: 'Digital Marketing',
      name: 'Starter SEO & Local Growth',
      badge: 'Marketing • Local SEO & Maps',
      desc: 'Dominate local Google Search, Google Maps, and nearby customer discovery.',
      originalPrice: '₹11,000',
      price: '₹8,999',
      period: '/ month',
      renewal: 'Billed monthly • Cancel anytime',
      popular: false,
      capacity: 'Local Map Pack & 10 Keywords',
      features: [
        'Google Business Profile (GBP) Full Optimization',
        'Local Citation Building & NAP Consistency Audit',
        'Targeted 10 High-Intent Local Keywords',
        'On-Page Technical SEO Audit & Fixes',
        'Monthly 4 Branded Social Media Post Creatives',
        'Google Review Growth Funnel Setup',
        'Monthly Performance & Ranking PDF Report',
      ],
      notIncluded: [
        'Paid Google Search Ads Campaigns',
      ]
    },
    {
      id: 'it-mktg-leads-m',
      domain: 'Digital Marketing',
      name: 'Performance Lead Gen ⭐',
      badge: 'Marketing • High-Intent Leads',
      desc: 'Paid ad campaigns across Google Search & Meta designed for qualified sales inquiries.',
      originalPrice: '₹24,000',
      price: '₹18,999',
      period: '/ month',
      renewal: 'Billed monthly • Cancel anytime',
      popular: true,
      capacity: 'Google Ads + Meta Lead Funnel',
      features: [
        'Google Search & High-Intent Display Ad Campaigns',
        'Meta (Facebook & Instagram) Targeted Lead Ads',
        'High-Converting Landing Page Design & CRO Audit',
        'Direct WhatsApp Lead Routing & CRM Webhook Sync',
        'Negative Keyword Scrubbing & Daily Bid Tuning',
        'A/B Creative Split Testing & Copywriting',
        'Weekly Lead Cost & ROI Video Reporting',
      ],
      notIncluded: [
        'Omnichannel Brand Leadership',
      ]
    },
    {
      id: 'it-mktg-enterprise-m',
      domain: 'Digital Marketing',
      name: 'Full-Funnel Enterprise Growth',
      badge: 'Marketing • Market Leaders',
      desc: 'Omnichannel market dominance combining SEO, paid media, and dedicated growth team.',
      originalPrice: '₹46,000',
      price: '₹36,999',
      period: '/ month',
      renewal: 'Billed monthly • Cancel anytime',
      popular: false,
      capacity: 'Omnichannel B2B Dominance',
      features: [
        'Omnichannel Dominance: Google, Meta, LinkedIn & YouTube',
        'Comprehensive Technical SEO & Content Marketing Funnel',
        'Dedicated Performance Marketing Squad & Copywriter',
        'Automated Email & WhatsApp Retargeting Drip Flows',
        'Competitor Ad Spying & Market Share Acquisition',
        'Conversion Rate Optimization (CRO) & Heatmap Analysis',
        'Bi-Weekly Strategic Growth Consulting Call',
      ],
      notIncluded: []
    }
  ]
};

// ============================================================================
// 3. HARDWARE ACCESSORIES & ADD-ONS (Like er4u.in)
// ============================================================================
const HARDWARE_ADDONS = [
  {
    name: 'G-Track 360 AI Face Attendance & RFID Terminal ⭐',
    category: 'HRM Attendance Hardware',
    desc: 'Dual AI-camera for sub-second facial match + RFID reader & fingerprint sensor. Auto cloud-syncs live punches to G-Track 360 HRM.',
    originalPrice: '₹22,999',
    price: '₹15,999',
    icon: ShieldCheck,
    popular: true
  },
  {
    name: 'Access Card Electronic Door Locking System',
    category: 'Electronic Access Control',
    desc: 'Heavy-duty electromagnetic lock (EM Lock) / drop bolt with RFID smart card reader, exit switch button, and power supply unit. Integrates with G-Track 360 for automated secure door opening upon employee card swipe.',
    originalPrice: '₹9,999',
    price: '₹6,999',
    icon: Lock,
    popular: true
  },
  {
    name: 'NFC Non-Tearable Hard Business Card & Standee',
    category: 'Smart Digital Identity',
    desc: 'High-durability waterproof NFC smart business card + Google review / payment counter standee. 1-tap phone connection.',
    originalPrice: '₹2,799',
    price: '₹1,699',
    icon: CreditCard,
    popular: false
  },
  {
    name: 'Smart NFC & QR Business Counter Standee',
    category: 'Counter & Retail Branding',
    desc: 'Custom-branded acrylic table standee with integrated contactless NFC chip + QR code for instant Google reviews, social media followings, digital menus, and payment links at checkout desks.',
    originalPrice: '₹3,200',
    price: '₹2,199',
    icon: QrCode,
    popular: false
  },
  {
    name: 'Portable Wireless Calling Bell',
    category: 'Service & Pager System',
    desc: 'Long-range wireless calling bell with digital chime receiver. Perfect for office reception, clinic cabins, and retail cash counters.',
    originalPrice: '₹4,499',
    price: '₹3,499',
    icon: Bell,
    popular: false
  },
  {
    name: 'Wireless Handheld Android POS Terminal',
    category: 'POS Terminal Machine',
    desc: 'Handheld 4G + Wi-Fi Android device with built-in high-speed thermal receipt printer. Ideal for delivery, restaurant tables & field billing.',
    originalPrice: '₹27,999',
    price: '₹19,999',
    icon: Smartphone,
    popular: true
  },
  {
    name: 'All-in-One Touch POS Terminal Machine',
    category: 'POS Desktop Station',
    desc: '15.6-inch capacitive multi-touch terminal, Intel Core processor, SSD, durable aluminum stand. Complete heavy-duty retail counter station.',
    originalPrice: '₹52,000',
    price: '₹39,999',
    icon: Laptop,
    popular: false
  },
];

// ============================================================================
// 4. FREQUENTLY ASKED QUESTIONS (Like er4u.in)
// ============================================================================
const FAQS = [
  {
    q: 'How does renewal work from the 2nd year onwards?',
    a: 'We believe in 100% transparent pricing with zero surprise lock-ins. All 1-Year plans carry a very low annual maintenance & server renewal fee (starting at just ₹1,800 to ₹2,999/year depending on your plan). This covers continuous cloud server hosting, automated daily backups, tax rule updates, and technical support.'
  },
  {
    q: 'What is the difference between Monthly and Annual billing?',
    a: 'Monthly plans offer maximum flexibility with zero long-term commitment (cancel or adjust user count anytime). Annual plans include an upfront ~28% discount (equivalent to nearly 3 months free), free initial setup, priority onboarding, and locked-in renewal rates.'
  },
  {
    q: 'Can G-Track Billing work completely offline without internet?',
    a: 'Yes! Our Startup Offline edition stores your product catalog and transactions locally on your desktop or laptop. You can bill customers, print invoices, and scan barcodes with zero internet connectivity. When internet is connected, it can optionally back up to the cloud.'
  },
  {
    q: 'How does G-Track 360 HRM handle attendance for remote or field staff?',
    a: 'Employees can punch in directly using the G-Track mobile app with GPS geo-fencing (ensuring they are physically at their designated client site or branch) along with optional AI selfie face verification to prevent proxy clock-ins.'
  },
  {
    q: 'Is GST tax included in the listed plan amounts?',
    a: 'All displayed prices are exclusive of 18% GST. You will receive an official GST tax invoice allowing your business to claim full input tax credit (ITC) against your GSTIN.'
  },
  {
    q: 'Can we upgrade our plan or add more staff / outlets later?',
    a: 'Absolutely. You can easily upgrade to a higher tier at any time by paying the pro-rata price difference. Your existing data, employee profiles, and transaction records will remain completely intact.'
  }
];

const PricingPage = () => {
  const [activeProduct, setActiveProduct] = useState('hrm'); // 'hrm' | 'billing' | 'custom-it'
  const [billingCycle, setBillingCycle] = useState('yearly'); // 'yearly' | 'monthly'
  const [itDomainFilter, setItDomainFilter] = useState('all'); // 'all' | 'Web Solutions' | 'Mobile Apps' | 'Desktop Software' | 'Digital Marketing'
  const [openFaq, setOpenFaq] = useState(null);
  
  // Modal State for Booking Demo with selected plan
  const [selectedPlanModal, setSelectedPlanModal] = useState(null);
  const [modalStatus, setModalStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const currentPlans = activeProduct === 'hrm' 
    ? HRM_PLANS[billingCycle] 
    : activeProduct === 'billing'
    ? BILLING_PLANS[billingCycle]
    : itDomainFilter === 'all'
    ? CUSTOM_IT_PLANS[billingCycle]
    : CUSTOM_IT_PLANS[billingCycle].filter((p) => p.domain === itDomainFilter);

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setModalStatus('loading');
    setErrorMessage('');

    const formData = new FormData(e.target);
    const name = formData.get('name');
    const email = formData.get('email');
    const phone = formData.get('phone');
    const company = formData.get('company') || 'Not specified';
    const note = formData.get('note') || '';

    try {
      // 1. Save to Firebase Firestore
      await addDoc(collection(db, 'demos'), {
        name,
        email,
        phone,
        company,
        note,
        product: activeProduct === 'hrm' ? 'G-Track 360 HRM Software' : activeProduct === 'billing' ? 'G-Track Billing & POS Software' : 'Custom IT & Digital Growth Services',
        planName: selectedPlanModal.name,
        planPrice: selectedPlanModal.price,
        billingCycle: billingCycle === 'yearly' ? 'Annual Plan' : 'Monthly Plan',
        status: 'New',
        createdAt: serverTimestamp(),
        createdAtString: new Date().toLocaleString()
      });

      // 2. Trigger notification email to both emails
      await sendNotificationEmail({
        formType: 'Pricing Plan Demo Request',
        subject: `[Pricing Lead] ${name} requested demo for ${selectedPlanModal.name} (${activeProduct.toUpperCase()})`,
        data: {
          Full_Name: name,
          Email: email,
          Phone_Number: phone,
          Company_Name: company,
          Selected_Product: activeProduct === 'hrm' ? 'G-Track 360 HRM Platform' : activeProduct === 'billing' ? 'G-Track Billing & POS Software' : 'Custom IT & Digital Growth Services',
          Selected_Plan: selectedPlanModal.name,
          Plan_Price: `${selectedPlanModal.price} (${billingCycle})`,
          Customer_Note: note || 'None'
        }
      });

      setModalStatus('success');
    } catch (err) {
      console.error('Error submitting plan booking:', err);
      setErrorMessage(err.message || 'Submission failed. Please try again.');
      setModalStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-32 pb-24 text-slate-900 font-sans">
      <SEO
        title="Pricing Plans | G-Track 360 HRM & G-Track Billing Software"
        description="Transparent and affordable pricing for G-Track 360 HRM Software and G-Track Retail POS Billing. Choose annual or lifetime plans with zero hidden fees."
        path="/pricing"
      />

      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        
        {/* ================================================================= */}
        {/* HEADER & HERO SECTION */}
        {/* ================================================================= */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full mb-4 font-bold text-xs uppercase tracking-widest border border-blue-100">
            <ShieldCheck className="h-4 w-4 text-blue-600" />
            100% Transparent Enterprise Pricing
          </div>
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-black text-slate-950 tracking-tight leading-tight mb-4">
            Simple, Transparent Plans <br />
            <span className="text-gold-gradient">Built for Serious Growth.</span>
          </h1>
          <p className="text-slate-600 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
            Choose between our all-in-one <strong>G-Track 360 HRM Software</strong> or high-speed <strong>G-Track Retail & POS Billing Software</strong>. Available in flexible 1-year plans or one-time lifetime licenses.
          </p>
        </div>

        {/* ================================================================= */}
        {/* 1. PRODUCT SWITCHER TABS (HRM vs BILLING) */}
        {/* ================================================================= */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
          <button
            type="button"
            onClick={() => {
              setActiveProduct('hrm');
              setItDomainFilter('all');
            }}
            className={`w-full sm:w-auto px-6 py-3.5 rounded-2xl font-black text-sm transition-all duration-300 flex items-center justify-center gap-2.5 shadow-sm border ${
              activeProduct === 'hrm'
                ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-500/20 scale-105'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
            }`}
          >
            <Building2 className="h-4 w-4" />
            G-Track 360 HRM Software
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveProduct('billing');
              setItDomainFilter('all');
            }}
            className={`w-full sm:w-auto px-6 py-3.5 rounded-2xl font-black text-sm transition-all duration-300 flex items-center justify-center gap-2.5 shadow-sm border ${
              activeProduct === 'billing'
                ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-500/20 scale-105'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
            }`}
          >
            <ShoppingCart className="h-4 w-4" />
            G-Track Retail & POS Billing
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveProduct('custom-it');
              setItDomainFilter('all');
            }}
            className={`w-full sm:w-auto px-6 py-3.5 rounded-2xl font-black text-sm transition-all duration-300 flex items-center justify-center gap-2.5 shadow-sm border ${
              activeProduct === 'custom-it'
                ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-500/20 scale-105'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
            }`}
          >
            <Code2 className="h-4 w-4" />
            Custom IT &amp; Digital Services
          </button>
        </div>

        {/* ================================================================= */}
        {/* 2. BILLING CYCLE TOGGLE (Monthly vs Annual) */}
        {/* ================================================================= */}
        <div className="flex items-center justify-center mb-12">
          <div className="bg-white p-1.5 rounded-2xl border border-slate-200/90 shadow-xs flex items-center gap-1">
            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-blue-600 text-white shadow-xs font-black'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Monthly Billing
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('yearly')}
              className={`px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center gap-2 ${
                billingCycle === 'yearly'
                  ? 'bg-slate-950 text-white shadow-xs font-black'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <span>Annual Plans</span>
              <span className={`text-[10px] uppercase font-black px-2 py-0.5 rounded-md ${
                billingCycle === 'yearly'
                  ? 'bg-amber-400 text-slate-950'
                  : 'bg-emerald-100 text-emerald-800'
              }`}>
                Save ~28%
              </span>
            </button>
          </div>
        </div>

        {/* Product Headline Banner */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-xs mb-10 text-center flex flex-col items-center justify-center">
          <div className="max-w-3xl mx-auto flex flex-col items-center text-center">
            <div className="inline-flex items-center justify-center gap-2 text-xs font-black uppercase tracking-wider text-blue-700 mb-2">
              <span>Currently Viewing:</span>
              <span className="px-2 py-0.5 bg-blue-50 rounded-md border border-blue-200">
                {activeProduct === 'hrm' 
                  ? 'Workforce, Attendance & Payroll' 
                  : activeProduct === 'billing'
                  ? 'Retail POS, Invoicing & Inventory'
                  : 'Custom IT Engineering & Growth'}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-950">
              {activeProduct === 'hrm' 
                ? 'G-Track 360 HRM Platform' 
                : activeProduct === 'billing'
                ? 'G-Track Retail & POS Billing Software'
                : 'Custom IT Engineering & Digital Growth Services'}
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              {activeProduct === 'hrm'
                ? 'Automate biometric & mobile geofenced attendance, shifts, leaves, and compliant monthly payroll.'
                : activeProduct === 'billing'
                ? 'Hybrid dual-storage (100% offline billing + real-time cloud sync), fast barcode GST invoicing, and dedicated Owner Admin Portal to check live store sales from anywhere.'
                : 'Flexible monthly and annual subscription plans for high-converting websites, native mobile apps, offline ERP desktop systems, and qualified B2B lead generation.'}
            </p>
          </div>
        </div>

        {/* Custom IT Domain Filters (When viewing Custom IT Services) */}
        {activeProduct === 'custom-it' && (
          <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
            {[
              { id: 'all', label: 'All Services (12)' },
              { id: 'Web Solutions', label: 'Web Solutions (3)' },
              { id: 'Mobile Apps', label: 'Mobile Apps (3)' },
              { id: 'Desktop Software', label: 'Desktop Software (3)' },
              { id: 'Digital Marketing', label: 'Digital Marketing (3)' }
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setItDomainFilter(f.id)}
                className={`px-4 py-2 rounded-xl text-xs font-black transition-all ${
                  itDomainFilter === f.id
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'bg-white text-slate-600 hover:text-blue-700 border border-slate-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* ================================================================= */}
        {/* 3. PRICING CARDS GRID */}
        {/* ================================================================= */}
        <div className={`grid grid-cols-1 md:grid-cols-2 ${activeProduct === 'custom-it' && itDomainFilter !== 'all' ? 'lg:grid-cols-3 max-w-5xl mx-auto' : 'lg:grid-cols-4'} gap-6 mb-16`}>
          {currentPlans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-[2rem] bg-white border transition-all duration-300 flex flex-col justify-between p-6 sm:p-7 ${
                plan.popular
                  ? 'border-blue-600 ring-2 ring-blue-600/30 shadow-xl -translate-y-1 pt-9 sm:pt-10'
                  : 'border-slate-200 shadow-sm hover:border-slate-300 hover:shadow-md'
              }`}
            >
              {/* Popular Flag Badge */}
              {plan.popular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap z-10 bg-blue-600 text-white text-[10.5px] font-black uppercase tracking-wider px-4 py-1 rounded-full shadow-md">
                  Most Popular
                </div>
              )}

              <div>
                {/* Header */}
                <div className="mb-4">
                  <div className="min-h-[20px] flex items-center mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">
                      {plan.badge}
                    </span>
                  </div>
                  <h3 className="text-xl font-black text-slate-950 mb-1 min-h-[28px] flex items-center">{plan.name}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed min-h-[44px]">{plan.desc}</p>
                </div>

                {/* Capacity Pill */}
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-slate-100 text-slate-800 text-xs font-extrabold mb-5 min-h-[28px]">
                  <Users className="h-3.5 w-3.5 text-blue-600" />
                  {plan.capacity}
                </div>

                {/* Pricing Block */}
                <div className="mb-5 pb-5 border-b border-slate-100 min-h-[105px] flex flex-col justify-between">
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs text-slate-400 line-through font-bold">
                        {plan.originalPrice}
                      </span>
                      <span className="text-3xl sm:text-4xl font-black text-slate-950">
                        {plan.price}
                      </span>
                      <span className="text-xs text-slate-500 font-bold">
                        {plan.period}
                      </span>
                    </div>
                    <p className="text-[11px] text-emerald-600 font-semibold mt-1">
                      {plan.renewal}
                    </p>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">+ 18% GST Applicable (Input Credit Available)</p>
                </div>

                {/* Features List */}
                <div className="space-y-2.5 mb-6 text-xs">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Included Features:</p>
                  {plan.features.map((feat, i) => (
                    <div key={i} className="flex items-start gap-2 text-slate-700">
                      <div className="h-4 w-4 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="h-3 w-3 stroke-[3]" />
                      </div>
                      <span className="leading-snug">{feat}</span>
                    </div>
                  ))}

                  {plan.notIncluded && plan.notIncluded.length > 0 && (
                    <div className="pt-2 space-y-2 opacity-50">
                      {plan.notIncluded.map((feat, i) => (
                        <div key={i} className="flex items-start gap-2 text-slate-400">
                          <div className="h-4 w-4 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0 mt-0.5">
                            <X className="h-3 w-3 stroke-[2]" />
                          </div>
                          <span className="leading-snug line-through">{feat}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-4 border-t border-slate-100 space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPlanModal(plan);
                    setModalStatus('idle');
                    setErrorMessage('');
                  }}
                  className={`w-full py-3.5 rounded-xl font-black text-xs transition-all shadow-sm flex items-center justify-center gap-1.5 ${
                    plan.popular
                      ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-500/20'
                      : 'bg-slate-900 hover:bg-slate-800 text-white'
                  }`}
                >
                  Book Free Demo & Setup
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>

                <a
                  href={`https://wa.me/918610452001?text=Hello%20GlobalEPRC,%20I%20am%20interested%20in%20${encodeURIComponent(plan.name)}%20for%20${encodeURIComponent(activeProduct === 'hrm' ? 'G-Track 360 HRM' : 'G-Track Billing')}.%20Please%20share%20details.`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full py-2.5 rounded-xl font-bold text-[11px] text-emerald-700 hover:bg-emerald-50 transition border border-emerald-200 flex items-center justify-center gap-1.5"
                >
                  <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
                  Chat on WhatsApp
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* ================================================================= */}
        {/* COMMERCIAL TERMS & GUARANTEE BANNER (From Official Rate Card) */}
        {/* ================================================================= */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm mb-16">
          <div className="text-center max-w-2xl mx-auto mb-6 pb-4 border-b border-slate-100 flex flex-col items-center">
            <div className="inline-flex items-center gap-2 text-blue-600 mb-1">
              <ShieldCheck className="h-5 w-5" />
              <span className="text-xs font-black uppercase tracking-wider">Enterprise Terms</span>
            </div>
            <h4 className="text-xl sm:text-2xl font-black text-slate-900">Commercial Terms &amp; Service Guarantees</h4>
            <p className="text-xs text-slate-500 mt-1">Official terms governing software deployment, hardware warranties, and delivery standards.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs text-slate-700">
            <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80">
              <span className="font-black text-blue-700 block mb-1">1. Transparent Tax Invoicing</span>
              <p className="text-[11px] text-slate-600 leading-relaxed">All listed pricing excludes 18% GST. Tax invoices provided with 100% Input Tax Credit (ITC) eligibility.</p>
            </div>
            <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80">
              <span className="font-black text-blue-700 block mb-1">2. Rapid 24h Deployment</span>
              <p className="text-[11px] text-slate-600 leading-relaxed">Ready software (G-Track 360 / POS) setup and cloud onboarding provided within 24 business hours.</p>
            </div>
            <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80">
              <span className="font-black text-blue-700 block mb-1">3. 1-Year Hardware Warranty</span>
              <p className="text-[11px] text-slate-600 leading-relaxed">Biometric and POS hardware units carry a 1-Year manufacturer replacement warranty against defects.</p>
            </div>
            <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80">
              <span className="font-black text-blue-700 block mb-1">4. Flexible Billing Choice</span>
              <p className="text-[11px] text-slate-600 leading-relaxed">Monthly plans offer cancel-anytime freedom; Annual plans include ~28% discount and locked-in renewal rates.</p>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-center gap-6 text-xs">
            <span className="text-slate-500 font-medium">Direct Support &amp; Project Inquiries:</span>
            <div className="flex items-center gap-4 font-bold text-slate-800">
              <a href="https://wa.me/918610452001" target="_blank" rel="noreferrer" className="text-blue-700 hover:text-blue-800 flex items-center gap-1.5">
                <MessageCircle className="h-4 w-4 text-emerald-600" />
                Phone &amp; WhatsApp: +91 8610452001
              </a>
              <a href="mailto:globaleprc@gmail.com" className="text-slate-700 hover:text-blue-700">
                globaleprc@gmail.com
              </a>
            </div>
          </div>
        </div>

        {/* ================================================================= */}
        {/* 4. HARDWARE ACCESSORIES & ADD-ONS (Like er4u.in) */}
        {/* ================================================================= */}
        <div className="mb-16">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <span className="text-xs font-black uppercase tracking-wider text-blue-600">Complete Hardware Ecosystem</span>
            <h3 className="text-2xl sm:text-3xl font-black text-slate-950 mt-1">
              Compatible Hardware & POS Accessories
            </h3>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Plug-and-play accessories pre-configured and tested for seamless integration with G-Track software.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {HARDWARE_ADDONS.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-xs flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="h-12 w-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                        <Icon className="h-6 w-6" />
                      </div>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">
                        {item.category}
                      </span>
                    </div>
                    <h4 className="text-base font-bold text-slate-900 mb-1">{item.name}</h4>
                    <p className="text-xs text-slate-500 leading-relaxed mb-4">{item.desc}</p>
                  </div>
                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                    <div>
                      <span className="text-xs text-slate-400 block font-medium">One-Time Device</span>
                      <span className="text-lg font-black text-slate-900">{item.price}</span>
                    </div>
                    <a
                      href={`https://wa.me/918610452001?text=Hello%20GlobalEPRC,%20I%20want%20to%20order%20hardware:%20${encodeURIComponent(item.name)}.`}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold transition"
                    >
                      Enquire
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ================================================================= */}
        {/* 5. FREQUENTLY ASKED QUESTIONS */}
        {/* ================================================================= */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="text-center mb-8">
            <span className="text-xs font-black uppercase tracking-wider text-blue-600">Got Questions?</span>
            <h3 className="text-2xl sm:text-3xl font-black text-slate-950 mt-1">
              Frequently Asked Questions
            </h3>
          </div>

          <div className="space-y-3">
            {FAQS.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div 
                  key={idx} 
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden transition-all shadow-xs"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full p-5 text-left flex items-center justify-between gap-4 font-bold text-sm sm:text-base text-slate-900 hover:text-blue-600 transition"
                  >
                    <span>{faq.q}</span>
                    <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? 'rotate-180 text-blue-600' : 'text-slate-400'}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 pt-1 text-xs sm:text-sm text-slate-600 leading-relaxed border-t border-slate-100">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ================================================================= */}
        {/* 6. BOTTOM CTA BANNER */}
        {/* ================================================================= */}
        <div className="rounded-3xl bg-slate-950 p-8 sm:p-12 text-white text-center shadow-xl relative overflow-hidden">
          <div className="max-w-2xl mx-auto relative z-10">
            <h3 className="text-2xl sm:text-4xl font-black mb-3">
              Need a Custom Multi-Branch Plan or Quotation?
            </h3>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed mb-6">
              Our enterprise solution architects analyze your exact headcount, counter volume, and branch logistics to deliver a guaranteed custom quote.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setSelectedPlanModal({
                    name: `${activeProduct === 'hrm' ? 'G-Track 360 HRM' : 'G-Track Billing'} Custom Enterprise`,
                    price: 'Custom Quote'
                  });
                  setModalStatus('idle');
                }}
                className="w-full sm:w-auto px-8 py-3.5 bg-amber-400 hover:bg-amber-300 text-slate-950 rounded-xl font-black text-xs uppercase tracking-wider transition shadow-md"
              >
                Request Custom Quotation
              </button>
              <a
                href="https://wa.me/918610452001?text=Hello%20GlobalEPRC,%20I%20need%20a%20custom%20quotation%20for%20my%20business."
                target="_blank"
                rel="noreferrer"
                className="w-full sm:w-auto px-6 py-3.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl font-bold text-xs transition flex items-center justify-center gap-2"
              >
                <MessageCircle className="h-4 w-4 text-emerald-400" />
                WhatsApp Text: +91 8610452001
              </a>
            </div>
          </div>
        </div>

      </div>

      {/* ================================================================= */}
      {/* 7. PLAN BOOKING / DEMO POPUP MODAL */}
      {/* ================================================================= */}
      {selectedPlanModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-lg rounded-3xl p-6 sm:p-8 shadow-2xl relative max-h-[90vh] overflow-y-auto">
            
            <button
              type="button"
              onClick={() => setSelectedPlanModal(null)}
              className="absolute top-5 right-5 text-slate-400 hover:text-slate-800 p-1"
            >
              <X className="h-5 w-5" />
            </button>

            {modalStatus === 'success' ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-black text-slate-900 mb-2">Request Received!</h3>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed mb-6">
                  Thank you! Our technical team has received your demo & quotation request for <strong className="text-slate-900">{selectedPlanModal.name}</strong>. We will get in touch with you shortly.
                </p>
                <button
                  type="button"
                  onClick={() => setSelectedPlanModal(null)}
                  className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl text-xs"
                >
                  Close Window
                </button>
              </div>
            ) : (
              <div>
                <div className="mb-5">
                  <span className="text-[10px] font-black uppercase tracking-wider text-blue-600">
                    Schedule Software Demo & Quote
                  </span>
                  <h3 className="text-xl sm:text-2xl font-black text-slate-900 mt-1">
                    {selectedPlanModal.name}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Plan Price: <span className="font-bold text-slate-800">{selectedPlanModal.price}</span> ({billingCycle === 'yearly' ? 'Annual Plan' : 'Monthly Plan'})
                  </p>
                </div>

                {errorMessage && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 text-xs rounded-xl flex items-center gap-2 border border-red-200">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                <form onSubmit={handleBookingSubmit} className="space-y-3.5">
                  <div>
                    <label className="block text-[11px] font-bold uppercase text-slate-600 mb-1">
                      Full Name *
                    </label>
                    <input
                      name="name"
                      required
                      type="text"
                      placeholder="Your Full Name"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-bold uppercase text-slate-600 mb-1">
                        Work Email *
                      </label>
                      <input
                        name="email"
                        required
                        type="email"
                        placeholder="name@company.com"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase text-slate-600 mb-1">
                        Phone / WhatsApp *
                      </label>
                      <input
                        name="phone"
                        required
                        type="tel"
                        placeholder="+91 98765 43210"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold uppercase text-slate-600 mb-1">
                      Company / Store Name
                    </label>
                    <input
                      name="company"
                      type="text"
                      placeholder="e.g. Acme Retail or Tech Corp"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold uppercase text-slate-600 mb-1">
                      Specific Requirements / Outlets (Optional)
                    </label>
                    <textarea
                      name="note"
                      rows={2}
                      placeholder="Number of staff, counter outlets, or existing ERP..."
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={modalStatus === 'loading'}
                    className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-xs uppercase tracking-wider transition shadow-md flex items-center justify-center gap-2"
                  >
                    {modalStatus === 'loading' ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Submitting...
                      </>
                    ) : (
                      'Confirm & Schedule Demo'
                    )}
                  </button>

                  <p className="text-[10px] text-slate-400 text-center">
                    Your details are securely saved. We never spam.
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default PricingPage;

